/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { WidgetPage } from './widget-page-base';
import { iamLogin } from '../main';
import { iamLocalization } from '../localization';
import { iam } from '../iam';

export class LoginPage extends WidgetPage {
  public override localize(): void {
    super.localize();
    iamLocalization.addLocalization(() => {
      iamLogin.localizeElement('main-header', 'head');
      iamLogin.localizeElement('pageTitle', 'signInTitle');
      iamLogin.localizeElement('signUpText', 'mvisionAccountSignin');
      iamLogin.localizeElement('signUpNow', 'signUpNow');
      iamLogin.localizeElement('signUpLink', { attribute: 'href', localeSubstitution: true, localeSeparator: '-', string: iam.settings.signUpURL });
      this.loginWidget.localize();
    });
  }

  public override initialize(): void {
    // do all synchronous initialization first and tell widget to render, then asynchronous initialization will happen in initializeLoginPasswordWidget() method
    iamLogin.addLangOptions();
    document.getElementById('sub-head-link').addEventListener('click', () => {
      // "not foo@bar.com?" link takes user back to email page
      window.localStorage.removeItem('login_hint');
      iamLogin.showTransitioningWidgetPage();
      if (iamLogin.parseQuery(iam.Token.getLocation().search).no_login_hint) { // keep us in Auth Code flow
        if (iam.settings.debug) console.log('[Auth Code] clicking not foo@bar.com from Auth Code flow, maintaining querystrings');
        iamLogin.redirectBackToEmail(iamLogin.parseQuery(iam.Token.getLocation().search));
      } else {
        iamLogin.redirectBackToEmail();
      }
      return false;
    });

    setTimeout(() => {
      if (iam.settings.debug) console.log('No user sign in after 5 minutes. Redirecting back to email page... ');
      iamLogin.redirectBackToEmail();
    }, 5 * 60 * 1000);

    // Check for Relaystate in URL and email. If not found redirect back to IDP discovery
    if (!iamLogin.parseQuery(iam.Token.getLocation().search).RelayState) {
      if (iam.settings.debug) console.log('No relaystate found. Redirecting back to email page... ');
      iamLogin.redirectBackToEmail();
    } else if (!window.localStorage.getItem('login_hint')) {
      if (iam.settings.debug) console.log('No email found. Redirecting back to email page... ');
      // for Auth Code flow: request starts at AuthZ, Okta redirects to Login.html with no context, this puts us back on email.html WITH the login context
      iamLogin.redirectBackToEmail(iamLogin.parseQuery(iam.Token.getLocation().search));
    } else {
      if (iam.settings.debug) console.log('Show Login Widget');

      this.loginWidget.initialize({
        baseUrl: iam.settings.domain,
        username: window.localStorage.getItem('login_hint'),
        i18n: iam.settings.i18n,
        assets: {
          baseUrl: 'localization/widget',
          rewrite: (assetPath: string) => {
            return assetPath.substr(assetPath.lastIndexOf('/'));
          }
        },
        features: {
          rememberMe: false,
          selfServiceUnlock: false,
          showPasswordToggleOnSignInPage: true
        }
      }, null, null, null, (res: any) => {
        // widget has completed its workflow
        if (iam.settings.debug) console.log(res);

        if (res.status === 'FORGOT_PASSWORD_EMAIL_SENT') {
          // reset account initiated
          if (iam.settings.debug) console.log('FORGOT_PASSWORD_EMAIL_SENT');
          return;
        }
        if (res.status === 'UNLOCK_ACCOUNT_EMAIL_SENT') {
          // unlock account initiated
          if (iam.settings.debug) console.log('UNLOCK_ACCOUNT_EMAIL_SENT');
          return;
        }

        // user logged in successfully
        window.localStorage.removeItem('login_hint');
        iam.Token.setLocation(iam.settings.domain + '/login/sessionCookieRedirect?token=' + res.session.token + '&redirectUrl=' + iamLogin.parseQuery(iam.Token.getLocation().search).RelayState);
      }, (err: unknown) => {
        console.error(err);
        window.localStorage.removeItem('login_hint');
      });

      document.getElementById('needHelpSigningIn').addEventListener('click', event => {
        // take user to sign in help page
        iamLogin.showTransitioningWidgetPage();
        iam.Token.setLocation(iam.settings.signInHelp + '?version=_random_version&login_hint=' + encodeURIComponent(this.getUsername()));
        event.stopImmediatePropagation();
        return false;
      });
    }
  }

  public override validateForm() {
    this.loginWidget.validateForm();
  }

  public override localeChanged(locale: string) {
    super.localeChanged(locale);
    this.loginWidget.localeChanged(locale);
  }
}
